/** @jsx jsx */
import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import {GatsbyImage, getImage} from 'gatsby-plugin-image';

import styled from '@emotion/styled';
import {css, jsx} from '@emotion/react';
import {Trans, useTranslation} from 'gatsby-plugin-react-i18next';

import {PMono, P2} from '../common/Typography';
import {colors, fonts, fontWeights} from '../../styles/theme';
import {ScreenSizeContext} from '../../helpers/context';
import {
    liveStatus,
    onRequestStatus,
    paymentMethodTypes,
    paymentProviderTypes,
} from '../../helpers/constants/channels';
import {countries} from '../../helpers/constants/common';


/*
 * Constants
 */
const tableBreakpoint = 885;


/*
 * Private Elements
 */
const StyledTableDefaultMobileEntry = styled.td`
    display: flex;
    padding: 10px 0;
    border: 0;
    vertical-align: middle;

    ${P2} {
        display: inline;
        margin: 0;

        &.list-inline {
            // This makes ellipsis after 5 lines. This is not standard CSS (yet), but is accepted by major browsers
            display: -webkit-box;
            -webkit-line-clamp: 5;
            -webkit-box-orient: vertical;
            overflow: hidden;

            .list-inline-item {
                display: inline;
                line-height: 17px;

                &::after {
                    content: ', ';
                    display: inline;
                }

                &:last-of-type {
                    &::after {
                        display: none;
                    }
                }
            }
        }
    }
  
    &:hover {
        box-shadow: none;
    }

    &:nth-of-type(1) {
        padding-top: 35px;
    }

    .mobile-table-content {
        padding: 10px 20px;

        ${P2} {
            line-height: 14px;
        }
    }

    .mobile-table-header {
        padding: 10px 20px;
        border-top: 0;
        border-bottom: 0;

        font-family: ${fonts.monospace};
        font-size: 10px;
        line-height: 17px;
        font-weight: ${fontWeights.regular};
        color: ${colors.mediumGrey};
        text-transform: uppercase;
        letter-spacing: 0.83px;
        vertical-align: middle;

        ${P2} {
            margin: 0;
        }
    }

    @media (min-width: ${tableBreakpoint}px) {
        padding: 30px 18px;
    }

    .logo-image {
        height: 50px;
        width: 100px;
    }
`;

const TableDefaultMobileEntry = ({header, style = null, children}) => (
    <StyledTableDefaultMobileEntry>
        <div className="col-6 mobile-table-header">{header}</div>
        <div css={style} className="col-6 mobile-table-content">{children}</div>
    </StyledTableDefaultMobileEntry>
);

TableDefaultMobileEntry.propTypes = {
    header: PropTypes.string.isRequired,
    style: PropTypes.object,
    children: PropTypes.node.isRequired,
};

TableDefaultMobileEntry.defaultProps = {
    style: null,
};

const StyledTableDefaultDesktopEntry = styled.td`
    padding: 10px 20px;
    border: 0;
    vertical-align: middle;

    &:hover {
        box-shadow: none;
    }

    .wrapper {
        width: 100%;
        display: flex;

        ${P2} {
            display: inline;
            margin: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            &.list-inline {
                display: inline;

                .list-inline-item {
                    display: inline;

                    &::after {
                        content: ', ';
                        display: inline;
                    }

                    &:last-of-type {
                        &::after {
                            display: none;
                        }
                    }
                }
            }
        }
    }

    @media (min-width: ${tableBreakpoint}px) {
        padding: 30px 18px;
    }
  
    .logo-image {
        height: 50px;
        width: 100px;
    }
`;

const TableDefaultDesktopEntry = ({style = null, children}) => (
    <StyledTableDefaultDesktopEntry css={style}>
        <div className="wrapper">{children}</div>
    </StyledTableDefaultDesktopEntry>
);

TableDefaultDesktopEntry.propTypes = {
    style: PropTypes.object,
    children: PropTypes.node.isRequired,
};

TableDefaultDesktopEntry.defaultProps = {
    style: null,
};

const TableDefaultEntry = ({header, style = null, children}) => {
    const screenSize = useContext(ScreenSizeContext);

    // If mobile, render for it
    if (screenSize.width < tableBreakpoint) {
        return <TableDefaultMobileEntry header={header} style={style}>{children}</TableDefaultMobileEntry>;
    }

    // Otherwise, render for desktop
    return <TableDefaultDesktopEntry style={style}>{children}</TableDefaultDesktopEntry>;
};

TableDefaultEntry.propTypes = {
    header: PropTypes.string.isRequired,
    style: PropTypes.object,
    children: PropTypes.node.isRequired,
};

TableDefaultEntry.defaultProps = {
    style: null,
};

const baseStatusStyle = css`
    .status-wrapper {
        margin: 0;

        .status {
            &:before {
                content: '•';
                vertical-align: sub;
                font-size: 35px;
                line-height: 0;
            }
        }
    }
`;

const liveStatusStyle = css`
    ${baseStatusStyle};

    color: #7FD7B3;
`;

const onRequestStatusStyle = css`
    ${baseStatusStyle};

    color: #FFDA81;
`;

const TableStatusEntry = ({header, children}) => {
    let style = null;
    let content = children;

    if (children === liveStatus) {
        style = liveStatusStyle;
        content = <Trans>Live</Trans>;
    } else if (children === onRequestStatus) {
        style = onRequestStatusStyle;
        content = <Trans>On Request</Trans>;
    }

    return (
        <TableDefaultEntry header={header} style={style}>
            <div className="status-wrapper">
                <span className="status" />
                {' '}
                <P2>{content}</P2>
            </div>
        </TableDefaultEntry>
    );
};

TableStatusEntry.propTypes = {
    header: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

const TableLogoEntry = ({header, imageData, imageAlt}) => (
    <TableDefaultEntry header={header}>
        <GatsbyImage
            image={getImage(imageData)}
            alt={imageAlt}
            className="logo-image"
            imgStyle={{objectFit: 'contain', objectPosition: 'left center'}}
        />
    </TableDefaultEntry>
);

TableLogoEntry.propTypes = {
    header: PropTypes.string.isRequired,
    imageData: PropTypes.object.isRequired,
    imageAlt: PropTypes.string.isRequired,
};

const StyledChannelEntry = styled.tr`
    border: 0;
    border-bottom: 2px solid #F0F0F0;

    &:hover {
        box-shadow: 3px 1px 15px ${colors.grey};
        cursor: pointer;
    }
`;

const ChannelEntry = ({channel, clickGoTo = () => {}}) => {
    const {t} = useTranslation();

    const clickChanelEntry = channelId => () => clickGoTo(channelId);

    return (
        <StyledChannelEntry className="channel-entry" onClick={clickChanelEntry(channel.id)}>
            <TableDefaultEntry header={t('Shopper Country')}>
                <P2 as="ul" className="list-inline">
                    {channel.payment_method.countries.map(country => (
                        <li className="list-inline-item" key={country.name}>{countries[country.name]}</li>
                    ))}
                </P2>
            </TableDefaultEntry>
            <TableLogoEntry
                header={t('Provider Name')}
                imageData={channel.payment_provider.logo.localFile}
                imageAlt={channel.payment_provider.name}
            />
            <TableDefaultEntry header={t('Provider Type')}>
                <P2>{paymentProviderTypes[channel.payment_provider.type]}</P2>
            </TableDefaultEntry>
            <TableLogoEntry
                header={t('Payment Method Name')}
                imageData={channel.payment_method.logo.localFile}
                imageAlt={channel.payment_method.name}
            />
            <TableDefaultEntry header={t('Payment Method Type')}>
                <P2>{paymentMethodTypes[channel.payment_method.type]}</P2>
            </TableDefaultEntry>
            <TableStatusEntry header={t('Status')}>{channel.status}</TableStatusEntry>
        </StyledChannelEntry>
    );
};

ChannelEntry.propTypes = {
    channel: PropTypes.object.isRequired,
    clickGoTo: PropTypes.func,
};

ChannelEntry.defaultProps = {
    clickGoTo: () => {},
};


/*
 * Public Elements
 */
const StyledChannelsList = styled.table`
    width: 100%;
    table-layout: fixed;

    thead {
        background-color: ${colors.transparent};
        border-bottom: 0;

        tr {
            border-top: 0;
            border-bottom: 0;

            th {
                padding: 30px 18px;
                border-top: 0;
                border-bottom: 0;
                width: 16%;
                font-family: ${fonts.monospace};
                font-size: 13px;
                line-height: 15px;
                font-weight: ${fontWeights.regular};
                color: ${colors.mediumGrey};
                text-transform: uppercase;
                letter-spacing: 1px;
                vertical-align: baseline;

                ${P2} {
                    margin: 0;
                }
            }
        }
    }

    tbody {
        background-color: ${colors.white};
        border-top: 0;
        border-bottom: 0;

        tr {
            &.empty {
                background-color: ${colors.lightGrey};
                margin: auto;
                text-align: center;
                width: 100%;
                height: 150px;
            }
        }
    }
`;

const ChannelsList = ({channels, channelClick = () => {}}) => {
    const screenSize = useContext(ScreenSizeContext);

    const channelsRows = channels.map(channel => (
        <ChannelEntry channel={channel} key={channel.id} clickGoTo={channelClick} />
    ));

    // Add an empty message row if there are no channels
    if (!channelsRows.length) {
        channelsRows.push(
            <tr className="empty" key="empty">
                <td colSpan="8">
                    <PMono><Trans>No channels match the selected filters.</Trans></PMono>
                </td>
            </tr>,
        );
    }

    const tableHeader = screenSize.width < tableBreakpoint ? null : (
        <thead>
            <tr>
                <th scope="col"><Trans>Shopper Country</Trans></th>
                <th scope="col"><Trans>Provider Name</Trans></th>
                <th scope="col"><Trans>Provider Type</Trans></th>
                <th scope="col"><Trans>Payment Method Name</Trans></th>
                <th scope="col"><Trans>Payment Method Type</Trans></th>
                <th scope="col"><Trans>Status</Trans></th>
            </tr>
        </thead>
    );

    return (
        <StyledChannelsList>
            {tableHeader}

            <tbody>
                {channelsRows}
            </tbody>
        </StyledChannelsList>
    );
};

ChannelsList.propTypes = {
    channels: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    channelClick: PropTypes.func,
};

ChannelsList.defaultProps = {
    channelClick: () => {},
};


/*
 * Exports
 */
export default ChannelsList;
